import {Alert, Button, CircularProgress, Grid, IconButton, Snackbar as MSnackbar} from "@mui/material";
import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {AlertColor} from "@mui/material/Alert/Alert";
import Close from "@mui/icons-material/Close"
import {css} from "@emotion/css";

export const SnackContext = createContext({
    error: (input: string) => {},
    loading: (message?: string) => {},
    success: (message: string) => {},
    hide: () => {},
    showAction: (input: string, actions: Action[]) => {},
})

export function useSnackbar() {
    return useContext(SnackContext);
}

type QueueAction = "error" | "loading" | "success" | "hide"

type QueueItem = {
    type: QueueAction,
    message?: string;
    actions?: Action[]
}

type Action = {
    title: string
    callback: () => void
};

function newItem(type: QueueAction, message?: string, actions?: Action[]) {
    return {
        type,
        message,
        actions,
    }
}

const minTime = 100;
const minErrorTime = 5 * 1000;

function since(tm: number) {
    return Date.now() - tm
}

export function Snackbar(props: PropsWithChildren<{
    timeout: number;
}>) {
    const [severity, setSeverity] = useState<AlertColor>("info");
    const [message, setMessage] = useState<string|null>(null);
    const [loading, setLoading] = useState(false);
    const tLast = useRef(0);

    useEffect(() => {
        if(loading) return;

        let tm = setTimeout(() => {
            setMessage(null);
        }, props.timeout);

        return () => clearTimeout(tm);
    }, [loading, message, severity, props.timeout]);

    const queue = useRef<QueueItem[]>([]);
    const [hasQueued, setHasQueued] = useState(false);
    const [actions, setActions] = useState<Action[]>([]);

    const processItem = useCallback((item: QueueItem) => {
        tLast.current = Date.now();
        loadingRef.current = false;

        switch(item.type) {
            case "error":
                setSeverity("error")
                severityRef.current = "error"
                setLoading(false)
                setMessage(item.message || "Unknown Error");
                setActions(item.actions || [])
                break;
            case "loading":
                setSeverity("info")
                severityRef.current = "info"
                loadingRef.current = true;
                setLoading(true);
                setMessage(item.message || "Loading...");
                setActions(item.actions || [])
                break;
            case "success":
                severityRef.current = "success"
                setSeverity("success")
                setLoading(false)
                setMessage(item.message || "Success");
                setActions(item.actions || [])
                break;
            case "hide":
                severityRef.current = null;
                setLoading(false)
                setMessage(null);
                break;
        }
    }, []);

    useEffect(() => {
        if(!hasQueued) return;

        const i = setInterval(() => {
            for(let i = 0; i < 1000; i++) {
                if(severityRef.current === "error" && since(tLast.current) < minErrorTime) {
                    return; // wait for next
                }

                const item = queue.current.shift();
                if (!item) {
                    setHasQueued(false)
                    return;
                }

                if(queue.current.length > 1 && item.type === "loading") {
                    continue
                }

                processItem(item)
                return;
            }
        }, minTime);

        return () => clearInterval(i);
    }, [hasQueued, processItem]);

    const loadingRef = useRef(false);
    loadingRef.current = loading;

    const severityRef = useRef<typeof severity | null>(message === null ? null : severity);
    severityRef.current = message === null ? null : severity;

    const ctx = useMemo(() => {

        function handle(item: QueueItem) {
            let timeoutPassed = true; // no timeout for most items

            if(severityRef.current === "error" && since(tLast.current) < minErrorTime) {
                timeoutPassed = false;
            }

            const enoughTimePassed = (timeoutPassed || loadingRef.current);
            console.log("snack", enoughTimePassed, severityRef.current, since(tLast.current), minErrorTime, item);

            if(enoughTimePassed && queue.current.length === 0) {
                processItem(item)
            } else {
                queue.current.push(item);
            }

            // always start queue, just in case another item comes in
            setHasQueued(true)
        }

        return ({
            error: (message: string) =>  {
                handle(newItem("error", message));
            },
            loading: (message?: string) => {
                handle(newItem("loading", message));
            },
            success: (message: string) => {
                handle(newItem("success", message));
            },
            hide: () => {
                handle(newItem("hide"));
            },
            showAction: (input: string, actions: {
                title: string;
                callback: () => void
            }[]) => {
                handle(newItem("loading", input, actions));
            }
        })
    }, [processItem]);

    return (
        <SnackContext.Provider value={ctx}>
            {props.children}
            <MSnackbar open={message !== null}>
                <Alert variant="filled" severity={severity}
                       classes={{
                           message: messageCls,
                       }}
                       action={
                    loading && actions.length === 0 && (
                        <div style={{height: "84%", display: "flex", alignItems: "center"}}>
                            <span style={{color: "white", width: "1em", height: "1em"}}><CircularProgress size="small" color="inherit" style={{display: "block"}} /></span>
                        </div>)
                } onClose={!loading ? () => setMessage(null) : undefined}>
                    <Grid container spacing={1} alignItems="center" style={{minWidth: 175}}>
                        <Grid item xs>
                            {message}
                        </Grid>
                        {!loading && actions.length === 0 && <Grid item>
                            <IconButton style={{color: "white", margin: -10}} size="small" onClick={() => setMessage(null)}>
                                <Close fontSize="inherit" color="inherit" />
                            </IconButton>
                        </Grid>}
                        {actions.map(a => <Grid item key={a.title}>
                            <Button onClick={() => a.callback()} size="small" color={"secondary"}>
                                {a.title}
                            </Button>
                        </Grid>)}
                    </Grid>
                </Alert>
            </MSnackbar>
        </SnackContext.Provider>
    )
}

const messageCls = css({
    "div&": {
        overflow: "visible",
    }
})